import { AddOn, Resource } from 'store/resource/types';

import { BOOKING_STATUSES } from 'shared/consts';
import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import { TransactionDetails } from 'store/transactions/types';

/*
 * ⭐ THE ONE PLACE that decides whether a booking may carry post-booking add-ons.
 *
 * Used by BOTH the post-payment detour and the step itself. The detour alone is not enough: the step has
 * a URL, so a tenant reaching it directly — a reload, a back button, a pasted link — would otherwise skip
 * every rule and be able to submit against a booking whose payment never completed, or one that no longer
 * holds its room. A booking that already has add-ons is not barred here: that is the amend, and the
 * service gates it. See `hasOrderedAddOns`.
 *
 * Every condition fails closed. Anything unknown, unresolved or still loading answers NO.
 */

/*
 * `state.resource.resource` is a single slot shared by every resource read in the app, so its add-ons must
 * not be trusted until its identity is checked against the booking. Matched on id OR uuid, string-compared:
 * helix stores booking rows' `resource_id` in both shapes and its resource read accepts either.
 */
export const resourceMatchesBooking = (
  resource: { id?: number | string; uuid?: string } | undefined | null,
  bookingResourceId: number | string | undefined | null,
): boolean => {
  if (!resource || bookingResourceId === undefined || bookingResourceId === null || bookingResourceId === '') {
    return false;
  }

  const target = String(bookingResourceId);

  return String(resource.id) === target || String(resource.uuid) === target;
};

/*
 * The statuses that still represent a booking holding its room. `Pending` is included deliberately: a
 * booking awaiting landlord approval still occupies the slot, which is the assumption the whole add-ons
 * design rests on. Everything else — `Denied_*`, `Canceled`, `Late_Cancel`, `Pending_Payment` — is out.
 */
const LIVE_BOOKING_STATUSES: ReadonlyArray<string> = [BOOKING_STATUSES.ACCEPTED, BOOKING_STATUSES.PENDING];

export const isLiveBookingStatus = (status: unknown): boolean =>
  typeof status === 'string' && LIVE_BOOKING_STATUSES.includes(status);

interface EligibilityInput {
  booking: TransactionDetails | undefined | null;
  resource: Resource | undefined | null;
  adapterName: string | undefined | null;
  addOnsEnabled: boolean | undefined;
  isKioskMode: boolean | undefined;
}

/*
 * ⭐ EVERY CONDITION THAT CAN BE DECIDED WITHOUT READING THE ROOM, split out so a caller can find out
 * whether it is worth paying for that read at all.
 *
 * RES-1326 needs this: the booking receipt offers the add-on step, and the receipt renders for ANY
 * booking — including one opened from the my-bookings list months later. Driving the room read
 * unconditionally there would put a network request behind every receipt open. These conditions are all
 * local to the store, so they settle the common "no" for free and the read is only spent on a booking
 * that could actually carry add-ons.
 *
 * ⛔ It is NOT a second gate. `bookingAddOnsEligibility` is still the only verdict, and it is this
 * predicate plus the room.
 */
export const bookingAddOnsPrerequisites = ({
  booking,
  adapterName,
  addOnsEnabled,
  isKioskMode,
}: Omit<EligibilityInput, 'resource'>): boolean => {
  const details = booking?.details;
  /*
   * One day of a multi-day checkout, with nothing ordered on it yet. helix writes the group only when
   * a checkout made more than one booking, so presence is the whole test. An order already placed is
   * exempt: that is the amend, and refusing it would shut the picker on the order it is for.
   */
  const awaitsMultiDaySupport = Boolean(details?.checkout_group_uuid) && !hasOrderedAddOns(booking);

  return Boolean(
    /* Kiosk has no checkout surface and never shows a receipt for the step to sit beside. */
    !isKioskMode &&
      /* Native only — third-party add-ons ride the booking create. Unresolved reads as not-native. */
      adapterName === ResourceBookingEnum.NativeAdapter &&
      /* The permanent per-building lever, distinct from the temporary rollout flag. */
      addOnsEnabled &&
      /* RES-1473, lifted by RES-1463. */
      !awaitsMultiDaySupport &&
      details?.id !== undefined &&
      details?.id !== null &&
      /*
       * ⛔ ALLOW-LIST, not a deny-list. Only a booking that still holds its room may raise a vendor
       * request. Excluding `Pending_Payment` alone let every terminal status through — denied, cancelled
       * and late-cancelled — so a stale or pasted URL could have raised a live catering order against a
       * booking that no longer exists. An absent or unrecognised status fails closed.
       */
      isLiveBookingStatus(details?.status),
  );
};

/*
 * Whether this booking already carries an order — a presentation rule that stays with the offer, not a
 * clause of the predicate above: helix replaces `resource_booking_add_ons` rather than blind-creating,
 * so a re-submit is the amend, and barring it would shut the picker on the one booking an amend is for.
 * Not submitting twice is the service's call (`canOrderAddOns`, `amendRefusedReason`). Reads
 * `details.add_ons`, helix's echo on the booking, not `vendor_add_ons`; nothing populates both.
 *
 * Counts only rows carrying `catalog_item_uuid`: that table also holds fee markers, rate lines and
 * stored questions, and a length test would read one of those as an order and withhold the offer.
 * helix emits the field per vendor row and omits it everywhere else.
 */
export const hasOrderedAddOns = (booking: TransactionDetails | undefined | null): boolean =>
  Boolean(booking?.details?.add_ons?.some(addOn => addOn?.catalog_item_uuid));

export const bookingAddOnsEligibility = ({
  booking,
  resource,
  adapterName,
  addOnsEnabled,
  isKioskMode,
}: EligibilityInput): { isEligible: boolean; bookingId: string | null; addOns: Array<AddOn> } => {
  const details = booking?.details;
  const bookingId = details?.id !== undefined && details?.id !== null ? String(details.id) : null;
  const matchesRoom = resourceMatchesBooking(resource, details?.resource_id);
  const addOns = matchesRoom ? resource?.add_ons ?? [] : [];

  const isEligible = Boolean(
    bookingAddOnsPrerequisites({ booking, adapterName, addOnsEnabled, isKioskMode }) &&
      /*
       * The room in the store must BE the booked room, and it must actually offer something ORDERABLE.
       *
       * RES-1359: `addOns.length` was that test only while helix dropped a product whose notice window
       * had closed. It now returns them flagged, so a menu of entirely out-of-window products has a
       * non-zero length and would offer the tenant a step on which nothing can be ordered. The flagged
       * rows still render once the step is offered -- they are only excluded from deciding whether to
       * offer it at all.
       */
      addOns.some(addOn => !addOn?.unavailable_reason),
  );

  return { isEligible, bookingId, addOns };
};
